import type {
  Eia861ActivityStatus,
  Eia861AnnualObservation,
  Eia861CanonicalElectricityYear,
  Eia861NonresDisplayMode,
  Eia861PilotUtilityConfig,
  Eia861SectorYearValue,
} from './types';
import {
  assertBurbankEia861SourceReady,
  EIA861_EXPECTED_YEAR_MAX,
  EIA861_EXPECTED_YEAR_MIN,
  getBwpSourceCompleteness,
} from './sourceCompleteness';

const MWH_TO_KWH = 1000;

export function mwhToKwh(mwh: number): number {
  return Math.round(mwh * MWH_TO_KWH);
}

/** EIA-861 nonresidential kWh: total retail minus residential (includes industrial when reported). */
export function nonresidentialKwhFromObservation(obs: Eia861AnnualObservation): number {
  const res = mwhToKwh(obs.residentialMwh);
  const total = mwhToKwh(obs.totalMwh);
  const fromTotal = total - res;
  const ind = obs.industrialMwh ?? 0;
  const trans = obs.transportationMwh ?? 0;
  const other = obs.otherMwh ?? 0;
  const partsSum = mwhToKwh(obs.commercialMwh + ind + trans + other);
  if (Math.abs(partsSum - fromTotal) <= 1000) {
    return fromTotal;
  }
  return partsSum > 0 ? partsSum : fromTotal;
}

export function nonresDisplayModeForObservation(obs: Eia861AnnualObservation): Eia861NonresDisplayMode {
  const ind = obs.industrialMwh ?? 0;
  const trans = obs.transportationMwh ?? 0;
  const other = obs.otherMwh ?? 0;
  const resKwh = mwhToKwh(obs.residentialMwh);
  const comKwh = mwhToKwh(obs.commercialMwh);
  const totalKwh = mwhToKwh(obs.totalMwh);
  if (ind > 0 || trans > 0 || other > 0) return 'complete_nonresidential';
  if (Math.abs(resKwh + comKwh - totalKwh) <= 1000) return 'commercial_only';
  return 'complete_nonresidential';
}

function sortedObservations(obs: Eia861AnnualObservation[]): Eia861AnnualObservation[] {
  return [...obs].sort((a, b) => a.year - b.year);
}

function observedYearsSet(observations: Eia861AnnualObservation[]): Set<number> {
  return new Set(observations.map((o) => o.year));
}

function buildSectorSeries(
  years: number[],
  observations: Eia861AnnualObservation[],
  sector: 'residential' | 'commercial'
): Eia861SectorYearValue[] {
  assertBurbankEia861SourceReady();
  const obsByYear = new Map(
    observations.map((o) => [
      o.year,
      {
        kwh:
          sector === 'residential'
            ? mwhToKwh(o.residentialMwh)
            : nonresidentialKwhFromObservation(o),
        obs: o,
      },
    ])
  );
  const observedYears = Array.from(obsByYear.keys()).sort((a, b) => a - b);
  const firstY = EIA861_EXPECTED_YEAR_MIN;
  const lastY = EIA861_EXPECTED_YEAR_MAX;
  const first = obsByYear.get(firstY)!;
  const last = obsByYear.get(lastY)!;

  return years.map((year) => {
    const direct = obsByYear.get(year);
    if (direct) {
      return {
        year,
        kwh: direct.kwh,
        status: 'observed_eia861' as Eia861ActivityStatus,
        anchorYears: null,
        utilityName: direct.obs.utilityName,
        eiaUtilityId: direct.obs.eiaUtilityId,
        sourceFile: direct.obs.sourceFile,
      };
    }

    const prevObs = observedYears.filter((y) => y < year).pop();
    const nextObs = observedYears.find((y) => y > year);

    if (year < firstY) {
      return {
        year,
        kwh: first.kwh,
        status: 'flat_before_first_eia_observation',
        anchorYears: [firstY, firstY],
        utilityName: first.obs.utilityName,
        eiaUtilityId: first.obs.eiaUtilityId,
        sourceFile: first.obs.sourceFile,
      };
    }
    if (year > lastY) {
      return {
        year,
        kwh: last.kwh,
        status: 'flat_after_last_eia_observation',
        anchorYears: [lastY, lastY],
        utilityName: last.obs.utilityName,
        eiaUtilityId: last.obs.eiaUtilityId,
        sourceFile: last.obs.sourceFile,
      };
    }

    if (prevObs != null && nextObs != null) {
      const v0 = obsByYear.get(prevObs)!.kwh;
      const v1 = obsByYear.get(nextObs)!.kwh;
      const interp = v0 + ((v1 - v0) * (year - prevObs)) / (nextObs - prevObs);
      const ref = obsByYear.get(prevObs)!.obs;
      return {
        year,
        kwh: interp,
        status: 'interpolated_between_eia_observations',
        anchorYears: [prevObs, nextObs],
        utilityName: ref.utilityName,
        eiaUtilityId: ref.eiaUtilityId,
        sourceFile: ref.sourceFile,
      };
    }

    throw new Error(
      `EIA-861 gap at ${year} cannot be interpolated (source incomplete: ${JSON.stringify(getBwpSourceCompleteness().missing_source_years)})`
    );
  });
}

export function buildEia861CanonicalElectricitySeries(
  config: Eia861PilotUtilityConfig,
  years: number[]
): Eia861CanonicalElectricityYear[] {
  assertBurbankEia861SourceReady();
  const observations = sortedObservations(config.observations);
  const have = observedYearsSet(observations);
  for (let y = EIA861_EXPECTED_YEAR_MIN; y <= EIA861_EXPECTED_YEAR_MAX; y++) {
    if (!have.has(y)) {
      throw new Error(`Missing EIA-861 observation for required year ${y}`);
    }
  }

  const resSeries = buildSectorSeries(years, observations, 'residential');
  const comSeries = buildSectorSeries(years, observations, 'commercial');
  const obsByYear = new Map(observations.map((o) => [o.year, o]));

  return years.map((year, i) => {
    const obs = obsByYear.get(year);
    const mode = obs
      ? nonresDisplayModeForObservation(obs)
      : nonresDisplayModeForObservation(observations[observations.length - 1]);
    return {
      year,
      residential: resSeries[i],
      commercial: comSeries[i],
      nonresDisplayMode: mode,
      totalKwh: resSeries[i].kwh + comSeries[i].kwh,
    };
  });
}

export function matchPilotConfigByGeofips(
  geofips: number | string,
  registry: Eia861PilotUtilityConfig[]
): Eia861PilotUtilityConfig | null {
  const norm = String(geofips).replace(/^0+/, '') || '0';
  return registry.find((r) => r.geoid.replace(/^0+/, '') === norm) ?? null;
}
